import { memo, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface JoinAnnouncement {
  id: number;
  name: string;
}

const MAX_CONCURRENT = 3;
const LIFE_MS = 4500;

/**
 * Queue of ephemeral "✨ Ayşe katıldı" pills. Capped: a 300-guest join storm
 * shows at most a rotating MAX_CONCURRENT — the rest are dropped (the
 * presence stack carries the real count).
 */
export function useJoinAnnouncements() {
  const [announcements, setAnnouncements] = useState<JoinAnnouncement[]>([]);
  const nextIdRef = useRef(1);

  const enqueue = useCallback((names: string[]) => {
    setAnnouncements((prev) => {
      const room = MAX_CONCURRENT - prev.length;
      if (room <= 0) return prev;
      const added = names.slice(0, room).map((name) => ({ id: nextIdRef.current++, name }));
      return [...prev, ...added];
    });
  }, []);

  const remove = useCallback((id: number) => {
    setAnnouncements((prev) => prev.filter((a) => a.id !== id));
  }, []);

  return { announcements, enqueue, remove };
}

/**
 * Renders the pills with the existing .ss-bubble one-shot animation —
 * its prefers-reduced-motion override is already opacity-only.
 */
function JoinAnnouncementsImpl({
  announcements,
  onDone,
}: {
  announcements: JoinAnnouncement[];
  onDone: (id: number) => void;
}) {
  const { t } = useTranslation();
  if (announcements.length === 0) return null;
  return (
    <div className="pointer-events-none flex flex-col items-start gap-2">
      {announcements.map((a) => (
        <div
          key={a.id}
          className="ss-bubble flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 px-3.5 py-1.5 text-white/90 text-sm shadow-lg"
          style={{ ["--life" as string]: `${LIFE_MS}ms` } as React.CSSProperties}
          onAnimationEnd={() => onDone(a.id)}
        >
          <span aria-hidden>✨</span>
          {t("slideshow.joined", { name: a.name })}
        </div>
      ))}
    </div>
  );
}

export const JoinAnnouncements = memo(JoinAnnouncementsImpl);
